/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Coverage classification for the device. What agent_active attests depends on the org's matching mode and on whether this device matched by serial or by assigned-user email.
 */
export const CoverageBucket = {
  AgentActive: "agent_active",
  AgentStale: "agent_stale",
  AgentOtherDevice: "agent_other_device",
  NoAgent: "no_agent",
  NoEmail: "no_email",
  UnresolvedEmail: "unresolved_email",
  Missing: "missing",
} as const;
/**
 * Coverage classification for the device. What agent_active attests depends on the org's matching mode and on whether this device matched by serial or by assigned-user email.
 */
export type CoverageBucket = ClosedEnum<typeof CoverageBucket>;

/**
 * One device from a connected MDM's inventory, annotated with its agent-coverage bucket. What that bucket attests depends on the organization's matching mode: under device-level matching a device may be classified by its own hardware serial (this machine ran the agent) or, when no serial heartbeat exists for it, by its assigned-user email (only that user ran the agent somewhere). Under user-level matching only the latter applies.
 */
export type ManagedDevice = {
  /**
   * The device-agent heartbeat that classified this device: the machine's own under device-level matching, otherwise its assigned user's. Omitted when no agent has ever synced.
   */
  agentLastSeenAt?: Date | undefined;
  /**
   * Coverage classification for the device. What agent_active attests depends on the org's matching mode and on whether this device matched by serial or by assigned-user email.
   */
  coverageBucket: CoverageBucket;
  /**
   * The MDM's identifier for the device.
   */
  externalId: string;
  firstSeenAt: Date;
  /**
   * Device hostname.
   */
  hostname?: string | undefined;
  /**
   * Device row ID.
   */
  id: string;
  lastSeenAt: Date;
  /**
   * Last device check-in as reported by the MDM.
   */
  mdmLastCheckInAt?: Date | undefined;
  /**
   * When the device went absent from the MDM inventory. Omitted while present.
   */
  missingSince?: Date | undefined;
  /**
   * Operating system name.
   */
  osName?: string | undefined;
  /**
   * Operating system version.
   */
  osVersion?: string | undefined;
  /**
   * Provider the device was synced from.
   */
  provider: string;
  /**
   * Hardware serial number as reported by the MDM.
   */
  serialNumber?: string | undefined;
  /**
   * Assigned user's email exactly as the MDM reported it. Omitted when the MDM has no assignment.
   */
  userEmail?: string | undefined;
  /**
   * Resolved Speakeasy user for the assigned email. Omitted when the email is missing or does not resolve to an org member.
   */
  userId?: string | undefined;
};

/** @internal */
export const CoverageBucket$inboundSchema: z.ZodMiniEnum<
  typeof CoverageBucket
> = z.enum(CoverageBucket);

/** @internal */
export const ManagedDevice$inboundSchema: z.ZodMiniType<
  ManagedDevice,
  unknown
> = z.pipe(
  z.object({
    agent_last_seen_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    coverage_bucket: CoverageBucket$inboundSchema,
    external_id: z.string(),
    first_seen_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    hostname: z.optional(z.string()),
    id: z.string(),
    last_seen_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    mdm_last_check_in_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    missing_since: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    os_name: z.optional(z.string()),
    os_version: z.optional(z.string()),
    provider: z.string(),
    serial_number: z.optional(z.string()),
    user_email: z.optional(z.string()),
    user_id: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "agent_last_seen_at": "agentLastSeenAt",
      "coverage_bucket": "coverageBucket",
      "external_id": "externalId",
      "first_seen_at": "firstSeenAt",
      "last_seen_at": "lastSeenAt",
      "mdm_last_check_in_at": "mdmLastCheckInAt",
      "missing_since": "missingSince",
      "os_name": "osName",
      "os_version": "osVersion",
      "serial_number": "serialNumber",
      "user_email": "userEmail",
      "user_id": "userId",
    });
  }),
);

export function managedDeviceFromJSON(
  jsonString: string,
): SafeParseResult<ManagedDevice, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ManagedDevice$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ManagedDevice' from JSON`,
  );
}
